import {isEventProperty} from '@sail/engine';
import type {MotionProps} from '../types';

function isMergeable(value: object): boolean {
  return (
    typeof value === 'object' &&
    // Don't merge any instances of AnimationControls.
    !('mount' in value)
  );
}

export function mergeMotionProps(
  target: MotionProps,
  source: MotionProps,
): void {
  const keys = Object.keys(source) as (keyof MotionProps)[];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    switch (key) {
      case 'animate':
      case 'initial':
      case 'exit':
      case 'transition':
      case 'style' as string:
        // We shouldn't encounter `style` since it's omitted from the type, but
        // check for it just in case.
        const targetValue = target[key];
        const sourceValue = source[key];

        // If the two values are mergeable and the same type, then merge them.
        if (isMergeable(targetValue) && isMergeable(sourceValue)) {
          const targetIsArray = Array.isArray(targetValue);
          const sourceIsArray = Array.isArray(sourceValue);

          if (targetIsArray === sourceIsArray) {
            target[key] = targetIsArray
              ? targetValue.concat(sourceValue)
              : {...targetValue, ...sourceValue};
            break;
          }
        }
        // Otherwise, fall back to assignment.
        target[key] = source[key];
        break;
      default:
        if (isEventProperty(key) && target[key] && source[key]) {
          // Merge any event handlers.
          target[key] = (...args: unknown[]) => {
            target[key](...args);
            source[key](...args);
          };
        } else {
          // Default to reassigning the value.
          target[key] = source[key];
        }
    }
  }
}
